
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Button from '../base/Button';

export default function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Close menu on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeMobileMenu();
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when menu is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  return (
    <>
      <header className="bg-dark-900 shadow-lg sticky top-0 z-50 border-b border-dark-700">
        <div className="w-full px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Left Section - Logo & License Info */}
            <div className="flex items-center space-x-4 flex-shrink-0 min-w-0">
              <img 
                src="https://static.readdy.ai/image/9d1a81acaff8e51df20fb8b8c1aae197/bcdcde40e3d45c7721e4a634ef58f84e.png"
                alt="Greater Golden Horseshoe Mortgages"
                className="h-12 w-auto object-contain"
              />
              <div className="text-sm text-gray-300 whitespace-nowrap hidden sm:block">
                <div className="font-semibold text-white">Licensed Mortgage Agent</div>
                <div>Lic# 13121</div>
              </div>
            </div>
            
            {/* Center Section - Navigation Menu */}
            <nav className="hidden lg:flex items-center justify-center flex-1">
              <div className="flex items-center space-x-6">
                <Link
                  to="/"
                  className="text-white hover:text-red-400 transition-colors duration-300 font-medium px-3 py-2 rounded-md hover:bg-dark-800"
                >
                  Home
                </Link>
                <Link
                  to="/about"
                  className="text-white hover:text-red-400 transition-colors duration-300 font-medium px-3 py-2 rounded-md hover:bg-dark-800"
                >
                  About Me
                </Link>
                <Link
                  to="/neighbourhood-guides"
                  className="text-white hover:text-red-400 transition-colors duration-300 font-medium px-3 py-2 rounded-md hover:bg-dark-800 whitespace-nowrap"
                >
                  Neighbourhood Guides
                </Link>
                <Link
                  to="/refinance-options"
                  className="text-white hover:text-red-400 transition-colors duration-300 font-medium px-3 py-2 rounded-md hover:bg-dark-800 whitespace-nowrap"
                >
                  Refinance Options
                </Link>
                <Link
                  to="/contact"
                  className="text-white hover:text-red-400 transition-colors duration-300 font-medium px-3 py-2 rounded-md hover:bg-dark-800"
                >
                  Contact
                </Link>
              </div>
            </nav>
            
            {/* Mobile menu button */}
            <button 
              className="lg:hidden p-2 w-11 h-11 flex items-center justify-center rounded-md hover:bg-dark-800 transition-colors duration-200"
              onClick={toggleMobileMenu}
              aria-label="Toggle mobile menu"
              aria-expanded={isMobileMenuOpen}
            >
              <i className={`ri-${isMobileMenuOpen ? 'close' : 'menu'}-line text-2xl text-gray-300 transition-all duration-200`}></i>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={closeMobileMenu}
            aria-hidden="true"
          ></div>
          
          {/* Mobile Menu Drawer */}
          <div 
            className="fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-dark-900 shadow-2xl z-50 lg:hidden transform transition-transform duration-200 ease-out border-l border-dark-700"
            role="dialog"
            aria-modal="true"
            aria-label="Mobile navigation menu"
          >
            {/* Menu Header */}
            <div className="flex items-center justify-between p-6 border-b border-dark-700">
              <div className="flex items-center space-x-3">
                <img 
                  src="https://static.readdy.ai/image/9d1a81acaff8e51df20fb8b8c1aae197/bcdcde40e3d45c7721e4a634ef58f84e.png"
                  alt="Greater Golden Horseshoe Mortgages"
                  className="h-8 w-auto object-contain"
                />
                <div className="text-xs text-gray-300">
                  <div className="font-semibold text-white">Licensed Agent</div>
                  <div>Lic# 13121</div>
                </div>
              </div>
              <button 
                onClick={closeMobileMenu}
                className="p-2 w-11 h-11 flex items-center justify-center rounded-md hover:bg-dark-800 transition-colors duration-200"
                aria-label="Close mobile menu"
              >
                <i className="ri-close-line text-2xl text-gray-300"></i>
              </button>
            </div>
            
            {/* Menu Content */}
            <nav className="px-4 pt-4 pb-6 space-y-2">
              <Link
                to="/"
                className="text-white hover:text-red-400 hover:bg-dark-800 block px-4 py-3 text-base font-medium transition-all duration-300 rounded-md"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Home
              </Link>
              <Link
                to="/about"
                className="text-white hover:text-red-400 hover:bg-dark-800 block px-4 py-3 text-base font-medium transition-all duration-300 rounded-md"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                About Me
              </Link>
              <Link
                to="/neighbourhood-guides"
                className="text-white hover:text-red-400 hover:bg-dark-800 block px-4 py-3 text-base font-medium transition-all duration-300 rounded-md"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Neighbourhood Guides
              </Link>
              <Link
                to="/refinance-options"
                className="text-white hover:text-red-400 hover:bg-dark-800 block px-4 py-3 text-base font-medium transition-all duration-300 rounded-md"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Refinance Options
              </Link>
              <Link
                to="/contact"
                className="bg-red-600 hover:bg-red-700 text-white block px-4 py-3 text-base font-medium rounded-md transition-all duration-300 whitespace-nowrap text-center"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Contact
              </Link>
            </nav>
            
            {/* Call to Action in Mobile Menu */}
            <div className="mt-auto p-6 border-t border-dark-700 space-y-4">
              <a 
                href="tel:5196583026" 
                className="flex items-center justify-center p-4 text-lg font-medium text-gray-300 hover:text-red-400 hover:bg-dark-800 rounded-lg transition-all duration-200 min-h-[44px]"
                onClick={closeMobileMenu}
              >
                <i className="ri-phone-line mr-3 text-xl"></i>
                (519) 658-3026
              </a>
              <a 
                href="https://calendly.com/tjcmonteiro/free-mortgage-consultation-with-tony-monteiro"
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeMobileMenu}
              >
                <Button 
                  variant="primary" 
                  className="w-full whitespace-nowrap text-lg py-4"
                >
                  <i className="ri-calendar-line mr-2"></i>
                  Book a Call
                </Button>
              </a>
            </div>
          </div>
        </>
      )}
    </>
  );
}
